import React, { useEffect, useState } from 'react';

import { Button } from '@meu-mapa-nev/design-system';

export interface AvisoDeSessaoProps {
  /** Instante em que a sessão expira de forma definitiva. */
  readonly expiresAt: Date;
  readonly now: Date;
  readonly onExtend: () => void;
  readonly onEnd: () => void;
  /** Antecedência do aviso, em minutos. */
  readonly warnBeforeMinutes?: number;
}

function formatarRestante(ms: number): string {
  const minutos = Math.floor(ms / 60_000);
  const segundos = Math.floor((ms % 60_000) / 1_000);

  if (minutos > 0) {
    return `${String(minutos)} min ${String(segundos).padStart(2, '0')}s`;
  }
  return `${String(segundos)}s`;
}

/**
 * Aviso de expiração de sessão — mesmo componente de `acesso/AvisoDeSessao.tsx`,
 * copiado (não reaproveitado) porque `acesso/` será removido junto com o fluxo
 * OTP que esta pasta substitui.
 */
export const AvisoDeSessao: React.FC<AvisoDeSessaoProps> = ({
  expiresAt,
  now,
  onExtend,
  onEnd,
  warnBeforeMinutes = 5,
}) => {
  const [restante, setRestante] = useState(() => expiresAt.getTime() - now.getTime());

  useEffect(() => {
    setRestante(expiresAt.getTime() - now.getTime());
    const timer = setInterval(() => {
      setRestante(expiresAt.getTime() - Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [expiresAt, now]);

  const proximoDoFim = restante <= warnBeforeMinutes * 60_000;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--nev-space-3)',
        padding: 'var(--nev-space-3)',
        marginBottom: 'var(--nev-space-4)',
        borderRadius: 'var(--nev-radius-control)',
        backgroundColor: proximoDoFim
          ? 'var(--nev-color-warning-soft)'
          : 'var(--nev-background-surface)',
        border: '1px solid var(--nev-border-subtle)',
      }}
    >
      <p
        role="status"
        aria-live="polite"
        style={{
          margin: 0,
          fontFamily: 'var(--nev-font-body)',
          fontSize: '0.875rem',
          color: 'var(--nev-text-secondary)',
        }}
      >
        {restante <= 0 ? (
          'Sua sessão expirou. Entre novamente para continuar.'
        ) : proximoDoFim ? (
          <>Sua sessão expira em {formatarRestante(restante)}. Quer continuar?</>
        ) : (
          <>Você está em uma sessão ativa neste aparelho.</>
        )}
      </p>

      <div style={{ display: 'flex', gap: 'var(--nev-space-2)' }}>
        {proximoDoFim && restante > 0 && (
          <Button variant="primary" onClick={onExtend}>
            Continuar conectada
          </Button>
        )}
        <Button variant="outline" onClick={onEnd}>
          Encerrar sessão
        </Button>
      </div>
    </div>
  );
};
